import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useColorScheme, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, initials, LOCALES, type Locale, type NotificationSummary } from '@repo/shared';
import { Icon, InitialsAvatar, StatusChip } from '@repo/ui';
import { useTenant } from '../app/tenant';
import { useYear } from '../app/year';
import { AccountDialog } from '../auth/AccountDialog';
import { useLogout, useMe } from '../auth/session';
import { setLocale } from '../i18n';
import { api } from '../lib/api';
import { notificationText } from '../lib/notifications';

const hover = { '&:hover': { bgcolor: 'surface.container' } } as const;

export function TopBar({
  onToggleNav,
  onOpenSearch,
  compact,
}: {
  onToggleNav: () => void;
  onOpenSearch: () => void;
  compact: boolean;
}) {
  const { t, i18n } = useTranslation();
  const tenant = useTenant();
  const me = useMe();
  const { years, year, setYearId } = useYear();
  const { mode, systemMode, setMode } = useColorScheme();
  const dark = (mode === 'system' ? systemMode : mode) === 'dark';
  const navigate = useNavigate();
  const logout = useLogout();
  const qc = useQueryClient();
  const [menu, setMenu] = useState<{ kind: 'year' | 'lang' | 'notif' | 'user'; el: HTMLElement } | null>(
    null,
  );
  const open = (kind: 'year' | 'lang' | 'notif' | 'user') => (e: MouseEvent<HTMLElement>) =>
    setMenu({ kind, el: e.currentTarget });
  const close = () => setMenu(null);
  const [account, setAccount] = useState(false);
  const dir = useTheme().direction;
  const [themeTip, setThemeTip] = useState(false);

  const notif = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<NotificationSummary>('/notifications'),
    refetchInterval: 60_000,
  });
  const markRead = useMutation({
    mutationFn: () => api('/notifications/read', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const unread = notif.data?.unread ?? 0;
  const lang = (LOCALES as readonly string[]).includes(i18n.language) ? (i18n.language as Locale) : 'fr';

  const changeLocale = (l: Locale) => {
    close();
    void setLocale(l);
    void api('/me/preferences', { method: 'PATCH', body: { locale: l } }).catch(() => undefined);
  };

  return (
    <Box
      component="header"
      sx={{
        gridArea: 'top',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        pl: 1.5,
        pr: 2.5,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        minWidth: 0,
      }}
    >
      <IconButton aria-label={t('shell.menu')} onClick={onToggleNav} sx={hover}>
        <Icon name="menu" size={24} />
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: compact ? 0 : 180 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            font: "700 10px/1 'Inter Variable', Inter, sans-serif",
            flexShrink: 0,
          }}
        >
          {tenant.shortName}
        </Box>
        {!compact && (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: '16px' }}>
            <Typography component="span" sx={{ fontWeight: 600, fontSize: 13, lineHeight: '16px' }}>
              {tenant.shortName}
            </Typography>
            <Typography component="span" sx={{ fontSize: 11, lineHeight: '16px', color: 'text.tertiary' }}>
              {t('shell.productLine', {
                product: tenant.productName,
                // The space follows the role: finance and teachers do not work in « Scolarité ».
                space: t(
                  `shell.space.${
                    me.roles.some((r) => r === 'ADMIN_ECOLE' || r === 'AGENT_SCOLARITE')
                      ? 'scolarite'
                      : me.roles.includes('AGENT_FINANCE')
                        ? 'finance'
                        : 'teaching'
                  }`,
                ),
              })}
            </Typography>
          </Box>
        )}
      </Box>

      {year && !compact && (
        <ButtonBase
          onClick={open('year')}
          aria-label={t('shell.year')}
          aria-haspopup="menu"
          sx={{
            height: 36,
            pl: 1.5,
            pr: 1,
            borderRadius: '8px',
            border: 1,
            borderColor: 'outline',
            gap: 0.75,
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: 'text.primary',
          }}
        >
          <Icon name="event" size={18} sx={{ color: 'text.secondary' }} />
          {year.label}
          <Icon name="arrow_drop_down" size={20} sx={{ color: 'text.secondary' }} />
        </ButtonBase>
      )}

      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
        <ButtonBase
          onClick={onOpenSearch}
          aria-label={t('palette.label')}
          aria-keyshortcuts="Control+K Meta+K"
          sx={{
            height: 40,
            width: '100%',
            maxWidth: 520,
            borderRadius: '20px',
            bgcolor: 'surface.container',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            pl: 1.75,
            pr: 1,
            color: 'text.secondary',
            cursor: 'text',
            justifyContent: 'flex-start',
          }}
        >
          <Icon name="search" />
          <Box
            component="span"
            sx={{
              flex: 1,
              fontSize: 14,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'start',
            }}
          >
            {t('shell.search')}
          </Box>
          <Box
            component="span"
            sx={{
              height: 24,
              px: 1,
              borderRadius: '6px',
              border: 1,
              borderColor: 'outline',
              fontSize: 11,
              fontWeight: 600,
              display: { xs: 'none', sm: 'inline-flex' },
              alignItems: 'center',
              bgcolor: 'background.paper',
              direction: 'ltr',
            }}
          >
            {t('shell.searchShortcut')}
          </Box>
        </ButtonBase>
      </Box>

      <ButtonBase
        onClick={open('lang')}
        aria-label={t('shell.language')}
        aria-haspopup="menu"
        sx={{
          height: 36,
          px: 1.5,
          borderRadius: '18px',
          gap: 0.75,
          fontSize: 13,
          fontWeight: 600,
          color: 'text.secondary',
          ...hover,
        }}
      >
        <Icon name="translate" />
        {!compact && t(`languages.short.${lang}`)}
      </ButtonBase>

      <Tooltip
        title={dark ? t('shell.lightTheme') : t('shell.darkTheme')}
        open={themeTip}
        onOpen={() => setThemeTip(true)}
        onClose={() => setThemeTip(false)}
      >
        <IconButton
          aria-label={dark ? t('shell.lightTheme') : t('shell.darkTheme')}
          onClick={() => {
            // The label flips with the theme: hide it instead of showing the opposite action under the cursor.
            setThemeTip(false);
            setMode(dark ? 'light' : 'dark');
          }}
          sx={hover}
        >
          <Icon name={dark ? 'light_mode' : 'dark_mode'} size={22} />
        </IconButton>
      </Tooltip>

      <IconButton
        aria-label={`${t('shell.notifications')} — ${t('shell.notificationsCount', { count: unread })}`}
        onClick={open('notif')}
        sx={hover}
      >
        <Badge
          badgeContent={unread}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              minWidth: 16,
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              px: 0.5,
              color: '#fff',
              top: -2,
              insetInlineEnd: -2,
            },
          }}
        >
          <Icon name="notifications" size={22} />
        </Badge>
      </IconButton>

      <ButtonBase
        onClick={open('user')}
        aria-label={t('shell.account')}
        aria-haspopup="menu"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          paddingInlineStart: 1.5,
          borderInlineStart: 1,
          borderColor: 'divider',
          textAlign: 'start',
          borderRadius: 0,
        }}
      >
        <InitialsAvatar initials={initials(`${me.firstName} ${me.lastName}`)} size={36} />
        {!compact && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              lineHeight: '16px',
              maxWidth: 160,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            <Box
              component="span"
              sx={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {me.displayName}
            </Box>
            <Box
              component="span"
              sx={{ fontSize: 11, color: 'text.tertiary', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {me.position ?? t(`roles.${me.roles[0] ?? 'ADMIN_ECOLE'}`)}
            </Box>
          </Box>
        )}
      </ButtonBase>

      <Menu anchorEl={menu?.el} open={menu?.kind === 'year'} onClose={close}>
        {years.map((y) => (
          <MenuItem
            key={y.id}
            selected={y.id === year?.id}
            onClick={() => {
              setYearId(y.id);
              close();
            }}
            sx={{ gap: 2, justifyContent: 'space-between', minWidth: 240 }}
          >
            <span className="tabular">{y.label}</span>
            <StatusChip
              tone={y.status === 'current' ? 'ok' : 'neutral'}
              label={t(`yearStatus.${y.status}`)}
            />
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={menu?.el} open={menu?.kind === 'lang'} onClose={close}>
        {LOCALES.map((l) => (
          <MenuItem key={l} selected={l === lang} onClick={() => changeLocale(l)} lang={l}>
            <ListItemIcon>{l === lang && <Icon name="check" />}</ListItemIcon>
            <ListItemText>{t(`languages.${l}`)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={menu?.el}
        open={menu?.kind === 'notif'}
        onClose={close}
        // Opens under the bell, towards the page: anchored on its start edge it ran past the screen edge.
        anchorOrigin={{ vertical: 'bottom', horizontal: dir === 'rtl' ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: dir === 'rtl' ? 'left' : 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="subtitle2">{t('shell.notifications')}</Typography>
          {unread > 0 && (
            <ButtonBase
              onClick={() => markRead.mutate()}
              sx={{
                color: 'primary.main',
                fontSize: 13,
                fontWeight: 600,
                px: 1,
                borderRadius: 2,
                whiteSpace: 'nowrap',
                minHeight: 32,
              }}
            >
              {t('shell.markAllRead')}
            </ButtonBase>
          )}
        </Box>
        <Divider />
        {(notif.data?.items ?? []).length === 0 && (
          <Box sx={{ px: 2, py: 2, color: 'text.tertiary', fontSize: 13 }}>{t('shell.noNotifications')}</Box>
        )}
        {(notif.data?.items ?? []).map((n) => {
          const text = notificationText(n, t);
          return (
            <MenuItem
              key={n.id}
              onClick={close}
              sx={{ alignItems: 'flex-start', gap: 1.5, whiteSpace: 'normal', py: 1 }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  mt: 0.75,
                  flexShrink: 0,
                  bgcolor: n.read ? 'transparent' : 'primary.main',
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box component="span" sx={{ fontWeight: n.read ? 400 : 600 }}>
                  {text.title}
                </Box>
                <Box component="span" sx={{ fontSize: 12, color: 'text.tertiary' }}>
                  {text.body} · <span dir="ltr">{formatDate(new Date(n.createdAt))}</span>
                </Box>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>

      <Menu anchorEl={menu?.el} open={menu?.kind === 'user'} onClose={close}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{me.displayName}</Typography>
          <Typography variant="caption" sx={{ color: 'text.tertiary' }}>
            {me.email ?? me.matricule}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            close();
            setAccount(true);
          }}
        >
          <ListItemIcon>
            <Icon name="manage_accounts" />
          </ListItemIcon>
          <ListItemText>{t('account.menu')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            logout.mutate(undefined, { onSettled: () => void navigate({ to: '/login' }) });
          }}
        >
          <ListItemIcon>
            <Icon name="logout" mirrorInRtl />
          </ListItemIcon>
          <ListItemText>{t('common.logout')}</ListItemText>
        </MenuItem>
      </Menu>
      {account && <AccountDialog onClose={() => setAccount(false)} />}
    </Box>
  );
}
